import classNames from "classnames";
import { AnimatePresence, motion } from "motion/react";
import { ElementRef, MutableRefObject, ReactNode, createContext, useContext, useRef, useState } from "react";
import { useDraggable } from "react-use-draggable-scroll";
import { IconButton } from "../IconButton";

interface AccordionContextType {
  isExpanded: boolean;
  toggle: () => void;
}

const AccordionContext = createContext<AccordionContextType | undefined>(undefined);

function useAccordionContext() {
  const context = useContext(AccordionContext);
  if (!context) {
    throw new Error(`Accordion compound components cannot be rendered outside the Accordion component`);
  }
  return context;
}

export function Header({ children, className }: { children?: ReactNode; className?: string }) {
  const { toggle } = useAccordionContext();
  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      className={classNames(
        "flex h-9 cursor-pointer items-center justify-between rounded-small border-none bg-background-90 px-3 font-bold outline-none",
        className,
      )}
      onClick={toggle}
    >
      {children}
    </div>
  );
}

export function HeaderText({ children, className }: { children?: string; className?: string }) {
  return <span className={classNames("font-style-label-l2", className)}>{children}</span>;
}

export function Icon({ className, isTertiary = false }: { className?: string; isTertiary?: boolean }) {
  const { isExpanded } = useAccordionContext();
  return (
    <motion.div
      className={`size-5 ${className}`}
      animate={{ rotate: isExpanded ? 180 : 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
    >
      <IconButton className="size-full" icon={"down"} isTertiary={isTertiary} />
    </motion.div>
  );
}

function MotionContent({ children, className }: { children?: React.ReactNode; className?: string }) {
  const ref = useRef<ElementRef<"div">>(null);
  const { events } = useDraggable(ref as MutableRefObject<ElementRef<"div">>);

  return (
    <motion.div
      ref={ref}
      {...events}
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: "auto", opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      className={classNames("custom-scrollbar overflow-hidden overflow-x-auto bg-transparent", className)}
    >
      {children}
    </motion.div>
  );
}

export function ContentWrapper({ children, className }: { children?: React.ReactNode; className?: string }) {
  const { isExpanded } = useAccordionContext();

  return (
    <AnimatePresence initial={false}>
      {isExpanded && <MotionContent className={className}>{children}</MotionContent>}
    </AnimatePresence>
  );
}

export function InnerContent({ children, className }: { children?: React.ReactNode; className?: string }) {
  return <div className={classNames("mt-5 flex w-full justify-center", className)}>{children}</div>;
}

function Accordion({
  children,
  className,
  isExpandedByDefault,
}: {
  children?: React.ReactNode;
  className?: string;
  isExpandedByDefault?: boolean;
}) {
  const [isExpanded, setIsExpanded] = useState<boolean>(isExpandedByDefault ?? false);
  const providerValue: AccordionContextType = {
    isExpanded,
    toggle: () => {
      setIsExpanded(!isExpanded);
    },
  };

  return (
    <AccordionContext.Provider value={providerValue}>
      <div className={classNames("mb-3 flex w-full flex-col border-none", className)} data-active={isExpanded}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

export { Accordion };
